import type { ArdoqId, ReferenceDirection } from '@ardoq/api-types';
import {
  FIELD_INFO_KEYS,
  FieldSource,
  type BubbleChartSources,
  type BubbleChartValueSource,
  type BubbleChartViewSettings,
  type ReferenceTypeSummary,
} from '@ardoq/bubble-chart';
import { WILDCARD } from '@ardoq/common-helpers';
import { toSourceOptions, type ToSourceOptionsArgs } from './sourceOptions';

const NO_VALUE_SOURCE: BubbleChartValueSource = { type: 'none' };

const ALL_COMPONENTS_VALUE_SOURCE: BubbleChartValueSource = {
  type: 'component',
  componentTypeIds: [WILDCARD],
};

const hasComponentAxisField = (
  viewSettings: BubbleChartViewSettings
): boolean =>
  FIELD_INFO_KEYS.some(key => {
    const field = viewSettings[key];
    return typeof field === 'string' && field !== '';
  });

const isReferenceTypeAvailable = (
  referenceTypeSummaries: ReferenceTypeSummary[],
  direction: ReferenceDirection,
  referenceTypeName: string
): boolean =>
  referenceTypeSummaries.some(
    summary =>
      summary.direction === direction &&
      summary.referenceTypeName === referenceTypeName
  );

const includesAnyBubbleType = (
  componentTypeIds: ArdoqId[],
  bubbleTypeIds: Set<ArdoqId>
): boolean =>
  componentTypeIds.includes(WILDCARD) ||
  componentTypeIds.some(componentTypeId => bubbleTypeIds.has(componentTypeId));

type ResolveValueSourceArgs = {
  viewSettings: BubbleChartViewSettings;
  bubbleTypeIds: Set<ArdoqId>;
  referenceTypeSummaries: ReferenceTypeSummary[];
};

/**
 * Settings saved before the source of values existed read their axis fields
 * from every bubble type.
 */
export const resolveValueSource = ({
  viewSettings,
  bubbleTypeIds,
  referenceTypeSummaries,
}: ResolveValueSourceArgs): BubbleChartValueSource => {
  const {
    fieldSource,
    selectedReferenceTypeName,
    selectedReferenceDirection,
    valueSourceComponentTypes,
  } = viewSettings;

  if (fieldSource === FieldSource.BY_REFERENCE) {
    if (
      !selectedReferenceTypeName ||
      !isReferenceTypeAvailable(
        referenceTypeSummaries,
        selectedReferenceDirection,
        selectedReferenceTypeName
      )
    ) {
      return NO_VALUE_SOURCE;
    }

    return {
      type: 'reference',
      direction: selectedReferenceDirection,
      referenceTypeName: selectedReferenceTypeName,
    };
  }

  if (
    valueSourceComponentTypes &&
    includesAnyBubbleType(valueSourceComponentTypes, bubbleTypeIds)
  ) {
    return { type: 'component', componentTypeIds: valueSourceComponentTypes };
  }

  return hasComponentAxisField(viewSettings)
    ? ALL_COMPONENTS_VALUE_SOURCE
    : NO_VALUE_SOURCE;
};

export const toValueComponentTypeIds = (
  valueSource: BubbleChartValueSource,
  bubbleTypeIds: Set<ArdoqId>
): Set<ArdoqId> => {
  if (
    valueSource.type !== 'component' ||
    valueSource.componentTypeIds.includes(WILDCARD)
  ) {
    return bubbleTypeIds;
  }

  return new Set(
    valueSource.componentTypeIds.filter(componentTypeId =>
      bubbleTypeIds.has(componentTypeId)
    )
  );
};

type ToBubbleChartSourcesArgs = ToSourceOptionsArgs & {
  viewSettings: BubbleChartViewSettings;
  bubbleTypeIds: Set<ArdoqId>;
};

export const toBubbleChartSources = ({
  viewSettings,
  bubbleTypeIds,
  ...sourceOptionsArgs
}: ToBubbleChartSourcesArgs): BubbleChartSources => {
  const options = toSourceOptions(sourceOptionsArgs);

  return {
    options,
    valueSource: resolveValueSource({
      viewSettings,
      bubbleTypeIds,
      referenceTypeSummaries: options.referenceTypeSummaries,
    }),
  };
};
